<script>
  import {
    flags,
    benefits,
    sections,
    ci,
    debug,
    saveKey,
    newestSection,
    layout,
    pages,
    currentPageIndex,
    usePasswordFile,
    password,
    hasAccess,
    overlay,
    useGlobalFloatingIcon
  } from "./store.js";
  import { SvelteToast } from "@zerodevx/svelte-toast";
  import { beforeUpdate, afterUpdate, onMount } from "svelte";
  import { fade } from "svelte/transition";
  import * as animateScroll from "svelte-scrollto";
  import Modal from "svelte-simple-modal";
  import GenericModalContent from "./helpers/GenericModalContent.svelte";

  let style = "";
  if ($ci.page.body.font) style += `--font: ${$ci.page.body.font};`;
  if ($ci.page.body.font_weight)
    style += `--fontWeight ${$ci.page.body.font_weight};`;
  if ($ci.page.body.font_style)
    style += `--fontStyle ${$ci.page.body.font_style};`;
  if ($ci.page.body.size) style += `--size: ${$ci.page.body.size};`;
  if ($ci.page.body.max_width)
    style += `--max-width: ${$ci.page.body.max_width};`;
  if ($ci.page.body.line_height)
    style += `--lineHeight: ${$ci.page.body.line_height};`;
  if ($ci.page.header.height)
    style += `--padding-top: ${$ci.page.header.height};`;

  let elBackground;

  let doPassCheck;
  // check password requirements
  (async () => {
    if (usePasswordFile) {
      // check localstorage
      let valid = await validPassword($password);

      if (valid) {
        doPassCheck = false;
        $hasAccess = true;
      } else {
        doPassCheck = true;
        $hasAccess = false;
      }
    } else {
      doPassCheck = false;
      $hasAccess = true;
    }
  })();

  async function validPassword(pw) {
    if (pw === "null" || pw === null) return false;

    let url = window.location.pathname
    let validPasswords = await fetch(url.substring(0, url.lastIndexOf('/')) + "/passwords.json", {
      mode: "cors",
      headers: { "Content-Type": "application/json" },
    }).then((res) => res.json());

    const getDate = () => {
      let d = new Date();
      return d.toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    };

    let d = getDate();
    if (validPasswords[d]) {
      // day exists
      let pws = validPasswords[d];
      return pws.includes(pw);
    } else {
      return false;
    }
  }

  afterUpdate(() => {
    if (layout === "fullscreen" && currentPage["background_image"]) {
      // bg image
      elBackground.style.setProperty(
        "background-image",
        "url(" + currentPage.background_image + ")"
      );
    } else if ($ci.page.color_background) {
      document.body.style.setProperty("--bg-color", $ci.page.color_background);
    }

    if ($ci.page.color_accent) {
      document.body.style.setProperty("--color-accent", $ci.page.color_accent);
    }
  });

  const toastOptions = {
    duration: 2000,
    reversed: true,
    intro: { x: 256 },
    theme: {
      "--toastProgressBackground":
        /*$ci.page.color_accent ?? */ "rgba(255,255,255,0.75)",
    },
  };

  import Header from "./Header.svelte";
  import Overlay from "./Overlay.svelte";
  import IENotice from "./helpers/IENotice.svelte";
  import ModalContent from "./helpers/ModalContent.svelte";

  //import * as Widgets from "./widgets";
  import Heading from "./widgets/Heading.svelte";
  import Image from "./widgets/Image.svelte";
  import Text from "./widgets/Text.svelte";
  import Puzzle from "./widgets/Puzzle.svelte";
  import Benefit from "./widgets/Benefit.svelte";
  import Audio from "./widgets/Audio.svelte";
  import Video from "./widgets/Video.svelte";
  import Link from "./widgets/Link.svelte";
  import Container from "./widgets/Container.svelte";
  import Details from "./widgets/Details.svelte";
  import Button from "./widgets/Button.svelte";
  import Margin from "./widgets/Margin.svelte";
  import Embed from "./widgets/Embed.svelte";
  import Carousel from "./widgets/Carousel.svelte";

  export const widgetDefs = {
    heading: Heading,
    image: Image,
    text: Text,
    puzzle: Puzzle,
    benefit: Benefit,
    audio: Audio,
    video: Video,
    link: Link,
    container: Container,
    details: Details,
    button: Button,
    margin: Margin,
    embed: Embed,
    carousel: Carousel,
  };

  function shouldShowSection(identifier, section, flags, benefits) {
    if (layout === "fullscreen") {
      // exclude everything that is not on the current page as not possible
      if (currentPage.sections.includes(identifier) === false) return false;
    }

    if (!section.hasOwnProperty("show_on")) return true;

    let show = true;
    if (section.show_on.flag) show = show && flags.has(section.show_on.flag);
    if (section.show_on.benefit)
      show = show && benefits.has(section.show_on.benefit);

    if (show) {
      // first time this section is shown --> do scroll
      $newestSection = identifier;
    }
    return show;
  }

  $: currentPage = layout === "fullscreen" ? pages[$currentPageIndex] : null;
  let pageIntroTime = 3000;
  if ($debug) pageIntroTime = 300;

  let newPageNoScroll = layout === "fullscreen";
  let newSectionTimeout = undefined;

  $: {
    $currentPageIndex,
      (() => {
        if (layout === "fullscreen") {
          console.log("new page, setting newpagenoscroll to true"),
            (newPageNoScroll = true);
          document.documentElement.scrollTop = 0;
          setTimeout(() => {
            newPageNoScroll = false;
          }, pageIntroTime + 100); //more than timeout for section
        }
      })(),
      console.log("NEW PAGE");
  }

  $: {
    $newestSection, // reactive: on change in newest section variable, trigger timeout
      (() => {
        newSectionTimeout = setTimeout(() => {
          if (newPageNoScroll === false) {
            console.log("scrolling to newest widget");
            animateScroll.scrollTo({
              element: `[data-id='${$newestSection}'`,
              offset: () => {
                return $ci.page.header.height
                  ? parseInt(
                      $ci.page.header.height.replace(/[^0-9]/g, ""),
                      10
                    ) * -1
                  : -100;
              },
            });
          }
        }, 100);
      })();
  }

  // show overlay when in fullscreen OR when in scroll with overlay content
  let showOverlay = false;
  $: {
    if (layout === "fullscreen") {
      showOverlay = currentPage.hasOwnProperty("hide_overlay") === false || (currentPage.hasOwnProperty("hide_overlay") === true && currentPage["hide_overlay"] === false)
    } else {
      showOverlay = (Object.keys(overlay.left).length > 0 || Object.keys(overlay.right).length > 0) || useGlobalFloatingIcon
    }
  }
  
  let isInternetExplorer = window.document.documentMode;
</script>

<svelte:head>
  <title>{$ci.strings.title ?? "Locked"}</title>
</svelte:head>

{#if isInternetExplorer}
  <IENotice />
{:else}
  {#if doPassCheck}
    <Modal
      key="password-modal"
      closeButton={false}
      closeOnEsc={false}
      closeOnOuterClick={false}
    >
      <ModalContent />
    </Modal>
  {/if}

  <GenericModalContent />

  <SvelteToast options={toastOptions} />

  {#if showOverlay}
    <Overlay />
  {/if}

  {#if layout !== "fullscreen"}
    <Header
      bgcolor={$ci.page.header.background_color}
      height={$ci.page.header.height}
      widgets={$ci.page.header.widgets}
      {style}
    />
  {/if}

  {#if layout === "fullscreen"}<div bind:this={elBackground} class="bg" />{/if}

  {#if $hasAccess}
    {#key $currentPageIndex}
      <main
        {style}
        in:fade={{
          delay: pageIntroTime,
          duration: layout === "fullscreen" ? 400 : 0,
        }}
        out:fade={{
          duration: layout === "fullscreen" ? 400 : 0,
        }}
        class:fullscreen={layout === "fullscreen"}
      >
        {#if $debug}
          <section class="debug" style="text-align: left;" data-id="debug">
            <div style="display: inline-block;">
              State: {JSON.stringify([...$flags])}<br />Benefits: {JSON.stringify(
                [...$benefits]
              )}
            </div>
            <button
              style="float: right; margin-right: 3rem"
              on:click={() => {
                localStorage.removeItem(`${saveKey}_benefits`);
                localStorage.removeItem(`${saveKey}_flags`);
                localStorage.removeItem(`${saveKey}_newest_section`);
                localStorage.removeItem(`${saveKey}_current_page_index`);
                localStorage.removeItem(`${saveKey}_simple_password`);
                location.reload();
              }}>Reset globals</button
            >
          </section>
        {/if}

        {#each Object.entries($sections) as [identifier, section]}
          {#if shouldShowSection(identifier, section, $flags, $benefits)}
            <section
              class="locked-section"
              class:debug={$debug}
              data-id={identifier}
              style={"--margin-top: " + section["margin_top"] ?? ""}
            >
              {#each section["widgets"] as widget, i}
                <div
                  class="locked-widget"
                  class:debug={$debug}
                  data-type={widget.type}
                  data-id={`${identifier}_${widget.type}_${i}`}
                >
                  <svelte:component
                    this={widgetDefs[widget.type]}
                    {...widget}
                    widget_id={`${identifier}_${widget.type}_${i}`}
                  />
                </div>
              {/each}
            </section>
          {/if}
        {/each}
      </main>
    {/key}
  {/if}
  {#if layout !== "fullscreen"}
    <div
      class="overscroll-container"
      style={`--overscroll: ${$ci.page.body.overscroll ?? 0}`}
    />
  {/if}
{/if}

<style type="text/scss">
  :root {
    --toastContainerTop: auto;
    /*--toastContainerLeft: auto;*/
    --toastContainerBottom: 1.5rem;
    /*--toastContainerRight: 2rem;*/
    --toastWidth: 24rem;
  }

  :global(body) {
    background-color: var(--bg-color);
  }

  // video in pop up fix
  :global(.window-wrap .locked-widget[data-type="video"]) {
    width: 100% !important;
  }

  :global(.bg) {
    background-image: none;
    background-size: cover;
    position: fixed;
    background-position: center;
    height: 100vh;
    width: 100vw;
    z-index: -1;

    -webkit-transition: background-image 1s ease-in-out;
    transition: background-image 1s ease-in-out;
  }

  main {
    padding: 0.1em;
    padding-top: var(--padding-top);

    text-align: center;
    max-width: var(--max-width);
    margin: 0 auto;

    font-family: var(--font);
    font-size: var(--size);
    font-weight: var(--fontWeight);
    font-style: var(--fontStyle);
    line-height: var(--lineHeight);

    box-shadow: -1px -1px 19px -3px rgba(0, 0, 0, 0.75);
    background-color: rgba(250, 245, 231, 1);

    &.fullscreen {
      padding-top: 0;
      background-color: rgba(250, 245, 231, 1);
    }
  }

  .overscroll-container {
    height: var(--overscroll, 0px);
    min-height: var(--overscroll, 0px);
    max-height: var(--overscroll, 0px);
  }

  .locked-section {
    padding: 2em;
    margin-top: var(--margin-top);
  }

  :global(.debug) {
    border: 1px dashed gray;
    position: relative;
  }
  :global(.debug:before) {
    content: attr(data-type);
    position: absolute;
    top: 0;
    right: 0;
    margin: 0 0.3em;
    font-size: 12px;
    background: rgba(250, 245, 231, 1);
  }
  :global(.locked-section.debug:before) {
    content: attr(data-id);
    left: 0;
  }

  @media (max-width: 960px) {
    main {
      max-width: 100%;
    }

    .locked-section {
      padding-left: 0.25em;
      padding-right: 0.25em;
    }
  }

  :global(.locked-hidden) {
    display: none !important;
  }
</style>
